import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';
import {
    DeployEntityRequestParams,
    GetBranchesParams,
    GetBuildDetailsParams,
    Stand,
    StandBranch,
    StandBuild,
    StandWebSocketMessage,
    StandWebSocketMessageBody,
    UpdateStandRequestParams,
    UpdateVMByStandIdRequestParams,
} from '@pages/stands/types';
import { GetDeployedSchemesForEntityParams } from '@pages/stands/store/sagas';
import { PaginatedList, RequestStatusEnum } from '@shared/types';
import { Scheme } from '@pages/schemes/types';

export type DeployEntityResponse = {
    pk: number;
    scheme_id: number;
    name: string;
} | null;

type StandsState = {
    deployEntityStatus: RequestStatusEnum;
    deployEntityError: AxiosError | null;
    deployEntityResponse: DeployEntityResponse;
    destroyStandStatus: RequestStatusEnum;
    destroyStandError: AxiosError | null;
    updateStandStatus: RequestStatusEnum;
    updateStandError: AxiosError | null;
    updateVMByStandIdStatus: RequestStatusEnum;
    updateVMByStandIdError: AxiosError | null;
    getStandsStatus: RequestStatusEnum;
    getStandsError: AxiosError | null;
    stands: Stand[];
    standsCount: number;
    deployedSchemesForEntity: Scheme[];
    getDeployedSchemesForEntityStatus: RequestStatusEnum;
    getDeployedSchemesForEntityError: AxiosError | null;
    branchesForDeploy: StandBranch[];
    branchesForDeployCount: number;
    buildsForDeploy: StandBuild[];
    getBranchesForDeployStatus: RequestStatusEnum;
    getBranchesForDeployError: AxiosError | null;
    getBuildsForDeployStatus: RequestStatusEnum;
    getBuildsForDeployError: AxiosError | null;
    getBuildDetailsStatus: RequestStatusEnum;
    getBuildDetailsError: AxiosError | null;
    buildDetails: [];
    standsBuilds: StandBuild[];
    standsBranches: StandBranch[];
    getStandsBuildsStatus: RequestStatusEnum;
    getStandsBuildsError: AxiosError | null;
    getStandsBranchesStatus: RequestStatusEnum;
    getStandsBranchesError: AxiosError | null;
};

const initialState: StandsState = {
    deployEntityStatus: RequestStatusEnum.NotRequested,
    deployEntityError: null,
    deployEntityResponse: null,
    destroyStandStatus: RequestStatusEnum.NotRequested,
    destroyStandError: null,
    updateStandStatus: RequestStatusEnum.NotRequested,
    updateStandError: null,
    updateVMByStandIdStatus: RequestStatusEnum.NotRequested,
    updateVMByStandIdError: null,
    getStandsStatus: RequestStatusEnum.NotRequested,
    getStandsError: null,
    stands: [],
    standsCount: 0,
    deployedSchemesForEntity: [],
    getDeployedSchemesForEntityStatus: RequestStatusEnum.NotRequested,
    getDeployedSchemesForEntityError: null,
    branchesForDeploy: [],
    branchesForDeployCount: 0,
    buildsForDeploy: [],
    getBranchesForDeployStatus: RequestStatusEnum.NotRequested,
    getBranchesForDeployError: null,
    getBuildsForDeployStatus: RequestStatusEnum.NotRequested,
    getBuildsForDeployError: null,
    getBuildDetailsStatus: RequestStatusEnum.NotRequested,
    getBuildDetailsError: null,
    buildDetails: [],
    standsBuilds: [],
    standsBranches: [],
    getStandsBuildsStatus: RequestStatusEnum.NotRequested,
    getStandsBuildsError: null,
    getStandsBranchesStatus: RequestStatusEnum.NotRequested,
    getStandsBranchesError: null,
};

const standsSlice = createSlice({
    name: '@stands',
    initialState: initialState,
    reducers: {
        deployEntityRequest(state, _action: PayloadAction<DeployEntityRequestParams>) {
            state.deployEntityStatus = RequestStatusEnum.Loading;
            state.deployEntityError = null;
            state.deployEntityResponse = null;
        },
        deployEntitySuccess(state, action) {
            state.deployEntityStatus = RequestStatusEnum.Success;
            state.deployEntityError = null;
            state.deployEntityResponse = action.payload;
        },
        deployEntityError(state, action: PayloadAction<AxiosError>) {
            state.deployEntityStatus = RequestStatusEnum.Error;
            state.deployEntityError = action.payload;
            state.deployEntityResponse = null;
        },
        deployEntityReset(state) {
            state.deployEntityStatus = RequestStatusEnum.NotRequested;
            state.deployEntityError = null;
            state.deployEntityResponse = null;
        },
        destroyStandRequest(state, _action: PayloadAction<Stand['pk']>) {
            state.destroyStandStatus = RequestStatusEnum.Loading;
            state.destroyStandError = null;
        },
        destroyStandSuccess(state) {
            state.destroyStandStatus = RequestStatusEnum.Success;
            state.destroyStandError = null;
        },
        destroyStandError(state, action: PayloadAction<AxiosError>) {
            state.destroyStandStatus = RequestStatusEnum.Error;
            state.destroyStandError = action.payload;
        },
        destroyStandReset(state) {
            state.destroyStandStatus = RequestStatusEnum.NotRequested;
            state.destroyStandError = null;
        },
        updateStandRequest(state, _action: PayloadAction<UpdateStandRequestParams>) {
            state.updateStandStatus = RequestStatusEnum.Loading;
            state.updateStandError = null;
        },
        updateStandSuccess(state) {
            state.updateStandStatus = RequestStatusEnum.Success;
            state.updateStandError = null;
        },
        updateStandError(state, action: PayloadAction<AxiosError>) {
            state.updateStandStatus = RequestStatusEnum.Error;
            state.updateStandError = action.payload;
        },
        updateStandReset(state) {
            state.updateStandStatus = RequestStatusEnum.NotRequested;
            state.updateStandError = null;
        },
        getStandsRequest(state) {
            state.getStandsStatus = RequestStatusEnum.Loading;
            state.getStandsError = null;
            state.stands = [];
        },
        getStandsSuccess(state, action: PayloadAction<PaginatedList<Stand>>) {
            const { items, count } = action.payload;
            state.getStandsStatus = RequestStatusEnum.Success;
            state.getStandsError = null;
            state.stands = items;
            state.standsCount = count;
        },
        getStandsError(state, action: PayloadAction<AxiosError>) {
            state.getStandsStatus = RequestStatusEnum.Error;
            state.getStandsError = action.payload;
            state.stands = [];
        },
        getStandsReset(state) {
            state.getStandsStatus = RequestStatusEnum.NotRequested;
            state.getStandsError = null;
            state.stands = [];
        },
        processWebSocketMessageAction(_state, _action: PayloadAction<StandWebSocketMessage>) {},
        removeStand(state, action: PayloadAction<StandWebSocketMessageBody>) {
            const { pk: standToRemoveId } = action.payload;

            state.stands = state.stands.filter((stand) => {
                const condition = stand.pk === standToRemoveId;

                return !condition;
            });
        },
        updateStandInfo(state, action: PayloadAction<StandWebSocketMessageBody>) {
            const standNewData = action.payload;
            const standIndex = state.stands.findIndex((stand) => stand.pk === standNewData.pk);

            if (standIndex === -1) {
                state.stands.push(standNewData as Stand);
            } else {
                let newLogsArray;

                const { logs } = state.stands[standIndex];

                if (standNewData.logs) {
                    newLogsArray = logs ? [...logs, ...standNewData.logs] : standNewData.logs;
                }

                const newStandData = {
                    ...state.stands[standIndex],
                    ...standNewData,
                };

                if (newLogsArray) {
                    newStandData.logs = newLogsArray;
                }

                state.stands[standIndex] = newStandData;
            }
        },
        clearStandLogs(state, action: PayloadAction<Stand['pk']>) {
            const standId = action.payload;
            const standIndex = state.stands.findIndex((stand) => stand.pk === standId);
            state.stands[standIndex] = {
                ...state.stands[standIndex],
                logs: [],
            };
        },
        getDeployedSchemesForEntityRequest(state, _action: PayloadAction<GetDeployedSchemesForEntityParams>) {
            state.getDeployedSchemesForEntityStatus = RequestStatusEnum.Loading;
            state.deployedSchemesForEntity = [];
            state.getDeployedSchemesForEntityError = null;
        },
        getDeployedSchemesForEntitySuccess(state, action: PayloadAction<Scheme[]>) {
            state.getDeployedSchemesForEntityStatus = RequestStatusEnum.Success;
            state.deployedSchemesForEntity = action.payload;
            state.getDeployedSchemesForEntityError = null;
        },
        getDeployedSchemesForEntityError(state, action: PayloadAction<AxiosError>) {
            state.getDeployedSchemesForEntityStatus = RequestStatusEnum.Error;
            state.deployedSchemesForEntity = [];
            state.getDeployedSchemesForEntityError = action.payload;
        },
        getDeployedSchemesForEntityReset(state) {
            state.getDeployedSchemesForEntityStatus = RequestStatusEnum.NotRequested;
            state.getDeployedSchemesForEntityError = null;
            state.deployedSchemesForEntity = [];
        },
        getBranchesForDeployRequest(state, _action: PayloadAction<GetBranchesParams>) {
            state.getBranchesForDeployStatus = RequestStatusEnum.Loading;
            state.getBranchesForDeployError = null;
        },
        getBranchesForDeploySuccess(state, action: PayloadAction<PaginatedList<StandBranch>>) {
            state.getBranchesForDeployStatus = RequestStatusEnum.Success;
            state.getBranchesForDeployError = null;
            state.branchesForDeploy = [...state.branchesForDeploy, ...action.payload.items];
            state.branchesForDeployCount = action.payload.count;
        },
        getBranchesForDeployError(state, action) {
            state.getBranchesForDeployStatus = RequestStatusEnum.Error;
            state.getBranchesForDeployError = action.payload;
            state.branchesForDeploy = [];
        },
        getBranchesForDeployReset(state) {
            state.getBranchesForDeployStatus = RequestStatusEnum.NotRequested;
            state.getBranchesForDeployError = null;
            state.branchesForDeploy = [];
        },
        resetArrayBranchesForDeploy(state) {
            state.branchesForDeploy = [];
        },
        getBuildsForDeployRequest(state, _action: PayloadAction<string>) {
            state.getBuildsForDeployStatus = RequestStatusEnum.Loading;
            state.getBuildsForDeployError = null;
            state.buildsForDeploy = [];
        },
        getBuildsForDeploySuccess(state, action) {
            state.getBuildsForDeployStatus = RequestStatusEnum.Success;
            state.getBuildsForDeployError = null;
            state.buildsForDeploy = action.payload.data;
        },
        getBuildsForDeployError(state, action) {
            state.getBuildsForDeployStatus = RequestStatusEnum.Error;
            state.getBuildsForDeployError = action.payload;
            state.buildsForDeploy = [];
        },
        getBuildsForDeployReset(state) {
            state.getBuildsForDeployStatus = RequestStatusEnum.NotRequested;
            state.getBuildsForDeployError = null;
            state.buildsForDeploy = [];
        },

        getBuildDetailsRequest(state, _action: PayloadAction<GetBuildDetailsParams>) {
            state.getBuildDetailsStatus = RequestStatusEnum.Loading;
            state.getBuildDetailsError = null;
            state.buildDetails = [];
        },
        getBuildDetailsSuccess(state, action) {
            state.getBuildDetailsStatus = RequestStatusEnum.Success;
            state.getBuildDetailsError = null;
            state.buildDetails = action.payload.data;
        },
        getBuildDetailsError(state, action) {
            state.getBuildDetailsStatus = RequestStatusEnum.Error;
            state.getBuildDetailsError = action.payload;
            state.buildDetails = [];
        },
        getBuildDetailsReset(state) {
            state.getBuildDetailsStatus = RequestStatusEnum.NotRequested;
            state.getBuildDetailsError = null;
            state.buildDetails = [];
        },
        updateVMByStandIdRequest(state, _action: PayloadAction<UpdateVMByStandIdRequestParams>) {
            state.updateVMByStandIdStatus = RequestStatusEnum.Loading;
            state.updateVMByStandIdError = null;
        },
        updateVMByStandIddSuccess(state) {
            state.updateVMByStandIdStatus = RequestStatusEnum.Success;
            state.updateVMByStandIdError = null;
        },
        updateVMByStandIdError(state, action: PayloadAction<AxiosError>) {
            state.updateVMByStandIdStatus = RequestStatusEnum.Error;
            state.updateVMByStandIdError = action.payload;
        },
        updateVMByStandIdReset(state) {
            state.updateVMByStandIdStatus = RequestStatusEnum.NotRequested;
            state.updateVMByStandIdError = null;
        },
        getStandsBranchesFilterRequest(state) {
            state.getStandsBranchesStatus = RequestStatusEnum.Loading;
            state.getStandsBranchesError = null;
        },
        getStandsBranchesFilterSuccess(state, action) {
            state.getStandsBranchesStatus = RequestStatusEnum.Success;
            state.getStandsBranchesError = null;
            state.standsBranches = action.payload.data;
        },
        getStandsBranchesFilterError(state, action) {
            state.getStandsBranchesStatus = RequestStatusEnum.Error;
            state.getStandsBranchesError = action.payload;
            state.standsBranches = [];
        },
        getStandsBranchesFilterReset(state) {
            state.getStandsBranchesStatus = RequestStatusEnum.NotRequested;
            state.getStandsBranchesError = null;
            state.standsBranches = [];
        },

        getStandsBuildsFilterRequest(state, _action) {
            state.getStandsBuildsStatus = RequestStatusEnum.Loading;
            state.getStandsBuildsError = null;
            state.standsBuilds = [];
        },
        getStandsBuildsFilterSuccess(state, action) {
            state.getStandsBuildsStatus = RequestStatusEnum.Success;
            state.getStandsBuildsError = null;
            state.standsBuilds = action.payload.data;
        },
        getStandsBuildsFilterError(state, action) {
            state.getStandsBuildsStatus = RequestStatusEnum.Error;
            state.getStandsBuildsError = action.payload;
            state.standsBuilds = [];
        },
        getStandsBuildsFilterReset(state) {
            state.getStandsBuildsStatus = RequestStatusEnum.NotRequested;
            state.getStandsBuildsError = null;
            state.standsBuilds = [];
        },
    },
});

export default standsSlice.reducer;

export const {
    deployEntityRequest,
    deployEntitySuccess,
    deployEntityError,
    deployEntityReset,
    destroyStandRequest,
    destroyStandSuccess,
    destroyStandError,
    destroyStandReset,
    updateStandRequest,
    updateStandError,
    updateStandReset,
    updateStandSuccess,
    getStandsRequest,
    getStandsSuccess,
    getStandsError,
    processWebSocketMessageAction,
    removeStand,
    updateStandInfo,
    clearStandLogs,
    getDeployedSchemesForEntityRequest,
    getDeployedSchemesForEntitySuccess,
    getDeployedSchemesForEntityError,
    getDeployedSchemesForEntityReset,
    getBranchesForDeployRequest,
    getBranchesForDeploySuccess,
    getBranchesForDeployError,
    getBranchesForDeployReset,
    getBuildsForDeployRequest,
    getBuildsForDeploySuccess,
    getBuildsForDeployError,
    getBuildsForDeployReset,
    getBuildDetailsRequest,
    getBuildDetailsSuccess,
    getBuildDetailsError,
    getBuildDetailsReset,
    resetArrayBranchesForDeploy,
    updateVMByStandIdRequest,
    updateVMByStandIddSuccess,
    updateVMByStandIdError,
    updateVMByStandIdReset,
    getStandsBranchesFilterRequest,
    getStandsBranchesFilterSuccess,
    getStandsBranchesFilterError,
    getStandsBuildsFilterRequest,
    getStandsBuildsFilterSuccess,
    getStandsBuildsFilterError,
} = standsSlice.actions;
